import type { RefObject } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BookmarkIcon from '@assets/icons/learning-overview/pdf-reader/bookmark.svg';
import PrevIconActive from '@assets/icons/learning-overview/pdf-reader/chevron-back-active.svg';
import PrevIconInactive from '@assets/icons/learning-overview/pdf-reader/chevron-back-inactive.svg';
import ChevronDownIcon from '@assets/icons/learning-overview/pdf-reader/chevron-down.svg';
import NextIconActive from '@assets/icons/learning-overview/pdf-reader/chevron-next-active.svg';
import NextIconInactive from '@assets/icons/learning-overview/pdf-reader/chevron-next-inactive.svg';
import ChevronsLeftIcon from '@assets/icons/learning-overview/pdf-reader/chevrons-left.svg';
import ChevronsRightIcon from '@assets/icons/learning-overview/pdf-reader/chevrons-right.svg';
import DownloadIcon from '@assets/icons/learning-overview/pdf-reader/download.svg';
import ListBtnIcon from '@assets/icons/learning-overview/pdf-reader/list-btn.svg';
import PresentationModeIcon from '@assets/icons/learning-overview/pdf-reader/presentation-mode.svg';
import PrinterIcon from '@assets/icons/learning-overview/pdf-reader/printer.svg';
import RotateCCWIcon from '@assets/icons/learning-overview/pdf-reader/rotateCCW.svg';
import RotateCWIcon from '@assets/icons/learning-overview/pdf-reader/rotateCW.svg';
import ToolsIcon from '@assets/icons/learning-overview/pdf-reader/tools.svg';
import ZoomInIcon from '@assets/icons/learning-overview/pdf-reader/zoom-in.svg';
import ZoomOutIcon from '@assets/icons/learning-overview/pdf-reader/zoom-out.svg';
import type { ToolbarOption } from '@components/pdf-reader/constants.ts';
import {
    DEFAULT_TOOLBAR,
    PDF_HEIGHT_RECALCULATION_DELAYS_MS,
    SCALE_OPTIONS_VALUES,
    scaleOptions,
    SCROLL_BOTTOM_TOLERANCE_PX,
    SHORT_PDF_FULL_VISIBILITY_RATIO,
    SHORT_PDF_MAX_PAGES,
    TOOLBAR_OPTIONS,
} from '@components/pdf-reader/constants.ts';
import { PdfPage } from '@components/pdf-reader/PdfPage.tsx';
import {
    DrawerWrapper,
    Group,
    IconButton,
    Label,
    MenuItem,
    MinimizeButton,
    MinimizeIconWrapper,
    NumberInput,
    ReaderWrapper,
    ScaleSelect,
    SecondaryMenu,
    SecondaryMenuDivider,
    Separator,
    ThumbnailSpinner,
    ToggleActionIcon,
    ToolbarContainer,
    ToolbarRow,
    ViewerContainer,
} from '@components/pdf-reader/styled.ts';
import { Thumbnail } from '@components/pdf-reader/Thumbnail.tsx';
import { TEST_IDS } from '@constants/testIds.ts';
import { useClickOutside } from '@hooks/useClickOutside.ts';
import { useFullscreen } from '@hooks/useFullscreen.ts';
import { useMounted } from '@hooks/useMounted.ts';
import { useLingui } from '@lingui/react/macro';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
// virtual list helps to render heavy pdfs, more than 1000 pages with smooth scrolling
import VirtualList from 'rc-virtual-list';

import 'pdfjs-dist/web/pdf_viewer.css';

import {
    bookmarkCurrentPage,
    computeFitPageScale,
    computeFitWidthScale,
    computeVirtualListSidebarHeight,
    detectModernBrowser,
    downloadPdfFile,
    getPageOffsetFromHeights,
    getSidebarPageScrollerHeight,
    openPdfInNewTab,
    parsePageFromHash,
    READER_WRAPPER_ID,
    upsertPageHeight,
} from './helpers.ts';

// Configure PDF.js worker
GlobalWorkerOptions.workerSrc = pdfjsWorker;

export interface PdfViewerProps {
    fileUrl: string | Uint8Array;
    toolbarOptions?: ToolbarOption[];
    onScrollToEnd?: () => void;
    pdfTitle?: string;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
    fileUrl,
    toolbarOptions = DEFAULT_TOOLBAR,
    onScrollToEnd,
    pdfTitle = 'document',
}) => {
    const isMounted = useMounted();
    const { t } = useLingui();
    const scaleSelectOptions = scaleOptions.map(({ value, label }) => ({
        value,
        label: typeof label === 'function' ? label() : label,
    }));
    const containerRef = useRef<HTMLDivElement>(null);
    const hasShortPdfUserScrolledRef = useRef(false);

    const secondaryMenuRef = useRef<HTMLDivElement>(null);
    const { isFullscreen, enterFullscreen, exitFullscreen } = useFullscreen(containerRef as RefObject<HTMLElement>);

    const [isModernBrowser, setIsModernBrowser] = useState(true);

    const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
    const [pageNum, setPageNum] = useState(1);
    const [scale, setScale] = useState(1);
    const [scaleOption, setScaleOption] = useState<string>(SCALE_OPTIONS_VALUES.PAGE_WIDTH);
    const [rotation, setRotation] = useState(0);

    const [extraMenuOpen, setExtraMenuOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const [pageHeights, setPageHeights] = useState<number[]>([]);

    // this trick dynamically changes height of virtual list  to avoid visual glitch like gray bottom padding
    const [virtualListHeight, setVirtualListHeight] = useState<number>(0);
    const pdfPages = pdf?.numPages || 0;
    const DRAWER_WIDTH = 260;

    const virtualListData = useMemo(() => [...Array(pdfPages)].map((_, index) => index + 1), [pdfPages]);

    useClickOutside(secondaryMenuRef as RefObject<HTMLElement>, () => setExtraMenuOpen(false));

    // Check if browser old or modern
    useEffect(() => {
        setIsModernBrowser(detectModernBrowser());
    }, []);

    // Load PDF and enforce max size
    useEffect(() => {
        let cancelled = false;

        const loadingTask =
            typeof fileUrl === 'string' ? getDocument({ url: fileUrl }) : getDocument({ data: fileUrl });

        loadingTask.promise
            .then((doc) => {
                if (!cancelled) {
                    hasShortPdfUserScrolledRef.current = false;
                    setPdf(doc);
                    setPageNum(1);
                }
            })
            .catch((err) => console.error('Error loading PDF:', err));

        return () => {
            cancelled = true;
        };
    }, [fileUrl]);

    // 1) Added fitWidth helper:
    const fitWidth = useCallback(async () => {
        if (!pdf || !containerRef.current) return;
        const width = containerRef.current.clientWidth;
        const newScale = await computeFitWidthScale(pdf, pageNum, rotation, width);
        setScale(newScale);
    }, [pdf, pageNum, rotation]);

    // 2) Hook to re‑apply fitWidth on mount, option‑change, and rotation change:
    useEffect(() => {
        if (scaleOption === SCALE_OPTIONS_VALUES.PAGE_WIDTH) {
            void fitWidth();
        }
    }, [scaleOption, pdf, pageNum, rotation, fitWidth]);

    // 3) Window‑resize listener to keep width fit:
    useEffect(() => {
        const onResize = () => {
            if (scaleOption === SCALE_OPTIONS_VALUES.PAGE_WIDTH) void fitWidth();
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [scaleOption, fitWidth]);

    // 4) Updated scale‐select handler:
    const handleScaleChange = useCallback(
        async (value: string) => {
            setScaleOption(value);
            if (value === SCALE_OPTIONS_VALUES.PAGE_WIDTH) {
                await fitWidth();
            } else if (value === SCALE_OPTIONS_VALUES.PAGE_FIT) {
                if (!pdf || !containerRef.current) return;
                const { clientWidth, clientHeight } = containerRef.current;
                const newScale = await computeFitPageScale(pdf, pageNum, rotation, clientWidth, clientHeight);
                setScale(newScale);
            } else if (value === SCALE_OPTIONS_VALUES.PAGE_ACTUAL) {
                setScale(1);
            } else {
                const num = parseFloat(value);
                setScale(!isNaN(num) ? num : 1);
            }
        },
        [pdf, pageNum, rotation, fitWidth],
    );

    const checkDocumentEnd = useCallback(() => {
        // Get the PDF's scrollable container.
        const container = containerRef.current?.querySelector('.rc-virtual-list-holder') as HTMLElement | null;

        if (!container) return;

        // Find the canvas representing the last PDF page.
        const lastCanvas = container.querySelector<HTMLCanvasElement>(`canvas[data-page="${pdfPages}"]`);

        if (!lastCanvas) return;

        // Get the visible positions of the container and last page.
        const containerRect = container.getBoundingClientRect();
        const lastCanvasRect = lastCanvas.getBoundingClientRect();

        // Calculate how much of the last page is visible inside the container.
        const visibleHeight =
            Math.min(lastCanvasRect.bottom, containerRect.bottom) - Math.max(lastCanvasRect.top, containerRect.top);

        // Normalize the visible height so it can be compared against a fixed threshold.
        const visibilityRatio = Math.max(0, visibleHeight) / lastCanvasRect.height;

        const remainingToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        const isScrollable = container.scrollHeight > container.clientHeight + SCROLL_BOTTOM_TOLERANCE_PX;

        // Normalize the visible height so the threshold is independent of page size and zoom.
        // The 2px tolerance covers fractional scroll values caused by browser rounding after zoom/rendering.
        const isScrollAtBottom = remainingToBottom <= SCROLL_BOTTOM_TOLERANCE_PX;

        const isShortPdf = pdfPages <= SHORT_PDF_MAX_PAGES;
        const isRegularPdf = !isShortPdf; //isRegularPdf is when pdf has more than 3 pages
        const hasRequiredShortPdfUserAction = isShortPdf && hasShortPdfUserScrolledRef.current;
        const isDocumentCompletionAllowed = isRegularPdf || hasRequiredShortPdfUserAction;

        const isScrollableDocumentEndReached =
            isDocumentCompletionAllowed && isScrollable && isScrollAtBottom && visibilityRatio >= 0.8;

        // Exception for 1–2 pages PDFs.
        // They may not create an internal scrollbar, so scroll-bottom detection alone cannot work.
        // For short PDFs we require a real mouse wheel down attempt first to avoid auto-completion on initial render.
        const isShortPdfEndVisible =
            hasRequiredShortPdfUserAction &&
            !isScrollable &&
            lastCanvasRect.bottom <= containerRect.bottom + SCROLL_BOTTOM_TOLERANCE_PX &&
            visibilityRatio >= SHORT_PDF_FULL_VISIBILITY_RATIO;

        // Notify when the user has really reached the end of the document.
        if (isScrollableDocumentEndReached || isShortPdfEndVisible) {
            onScrollToEnd?.();
        }
    }, [pdfPages, onScrollToEnd]);

    // Add a ResizeObserver to trigger fitWidth immediately (and on any container resize)
    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(() => {
            if (scaleOption === SCALE_OPTIONS_VALUES.PAGE_WIDTH) {
                void fitWidth();
            }
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [fitWidth, scaleOption]);

    // Ensure fitWidth is also called once after PDF loads
    useEffect(() => {
        if (pdf && scaleOption === SCALE_OPTIONS_VALUES.PAGE_WIDTH) {
            void fitWidth();
        }
    }, [pdf, fitWidth, scaleOption]);

    // 1) Deep-link: parse #page= on load
    useEffect(() => {
        if (!pdf) return;
        const target = parsePageFromHash(window.location.hash, pdf.numPages);
        if (target) setPageNum(target);
    }, [pdf]);

    // For 1–2 pages PDFs we do not complete automatically on mount.
    // A real mouse wheel down attempt is required first.
    useEffect(() => {
        if (!pdf || pdfPages > SHORT_PDF_MAX_PAGES) return;

        const viewer = containerRef.current;
        if (!viewer) return;

        const handleShortPdfWheel = (event: WheelEvent) => {
            if (event.deltaY <= 0) return;

            if (!hasShortPdfUserScrolledRef.current) {
                hasShortPdfUserScrolledRef.current = true;
            }

            requestAnimationFrame(() => {
                checkDocumentEnd();
            });
        };

        viewer.addEventListener('wheel', handleShortPdfWheel, { passive: true, capture: true });
        return () => {
            viewer.removeEventListener('wheel', handleShortPdfWheel, true);
        };
    }, [pdf, pdfPages, scale, scaleOption, checkDocumentEnd]);

    // update value in NumberInput on change and scroll-to-end detection
    // Keeps pageNum in sync with the page visually centered in the viewer.
    // Also calls onScrollToEnd exactly once when the last page becomes centered.
    useEffect(() => {
        if (!pdf || !onScrollToEnd) return;

        const container = containerRef.current?.querySelector('.rc-virtual-list-holder') as HTMLElement | null;
        if (!container) return;

        let endNotified = false;

        // Local alias to avoid "page vs pages" confusion when comparing to the last page
        const totalPages = pdfPages;
        const isShortPdf = totalPages <= SHORT_PDF_MAX_PAGES;

        const handleScroll = () => {
            const canvases = container.querySelectorAll<HTMLCanvasElement>('canvas[data-page]');
            if (!canvases.length) return;

            // Find the page whose canvas center is closest to the container's center
            const containerTop = container.getBoundingClientRect().top;
            const containerHeight = container.clientHeight;

            let centeredPage = 1;
            let closestDistance = Infinity;

            canvases.forEach((canvas) => {
                const rect = canvas.getBoundingClientRect();
                const page = Number(canvas.getAttribute('data-page'));

                // Distance between canvas center and container center
                const canvasCenter = rect.top + rect.height / 2;
                const containerCenter = containerTop + containerHeight / 2;
                const distance = Math.abs(canvasCenter - containerCenter);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    centeredPage = page;
                }
            });

            // Special case: at 50% zoom, count the last page only if ≥80% visible
            if (scale === 0.5) {
                const lastCanvas = container.querySelector<HTMLCanvasElement>(`canvas[data-page="${totalPages}"]`);
                if (lastCanvas) {
                    const rect = lastCanvas.getBoundingClientRect();
                    const visibleHeight =
                        Math.min(rect.bottom, containerTop + containerHeight) - Math.max(rect.top, containerTop);
                    const visibilityRatio = visibleHeight / rect.height;

                    if (visibilityRatio >= 0.8) {
                        centeredPage = totalPages;
                    }
                }
            }

            setPageNum((prev) => (prev !== centeredPage ? centeredPage : prev));

            checkDocumentEnd();

            // Same 2px tolerance as checkDocumentEnd to keep bottom detection consistent.
            const isScrollAtBottom =
                container.scrollHeight - container.scrollTop - container.clientHeight <= SCROLL_BOTTOM_TOLERANCE_PX;

            // Notify once when the last page is centered.
            // For 1–2 pages PDFs, require a mouse wheel down attempt first to avoid auto-completion on initial render.
            if (
                !endNotified &&
                centeredPage === totalPages &&
                isScrollAtBottom &&
                (!isShortPdf || hasShortPdfUserScrolledRef.current)
            ) {
                onScrollToEnd();
                endNotified = true;
            }
        };

        container.addEventListener('scroll', handleScroll, { passive: true });

        // Trigger once on mount
        handleScroll();

        return () => container.removeEventListener('scroll', handleScroll);
    }, [pdf, pdfPages, scale, rotation, onScrollToEnd, checkDocumentEnd, scaleOption]);

    const handlePageHeight = useCallback(
        (page: number, height: number) => {
            setPageHeights((prev) => upsertPageHeight(prev, page, height));

            if (page === pdfPages) {
                requestAnimationFrame(() => {
                    checkDocumentEnd();
                });
            }
        },
        [checkDocumentEnd, pdfPages],
    );

    const scrollToPage = useCallback(
        (targetPage: number) => {
            const container = containerRef.current?.querySelector('.rc-virtual-list-holder') as HTMLElement | null;
            if (!container) return;

            container.scrollTop = getPageOffsetFromHeights(pageHeights, targetPage);
            setPageNum(targetPage);
        },
        [pageHeights],
    );

    useEffect(() => {
        // Recalculate virtual list height after initial mount and after PDF/layout updates.
        // On hard refresh, wrapper height, PDF fit-width scale, and rendered canvas height may settle asynchronously,
        // so we schedule a few extra recalculations to keep scrolling stable.
        const animationFrameIds: number[] = [];
        const timeoutIds: number[] = [];

        const updateHeight = () => {
            const fallbackHeight = getSidebarPageScrollerHeight(window.innerHeight, isFullscreen);
            const viewerHeight = containerRef.current?.getBoundingClientRect().height ?? 0;
            const nextHeight = viewerHeight > 0 ? Math.min(fallbackHeight, viewerHeight) : fallbackHeight;

            setVirtualListHeight((prev) => (prev === nextHeight ? prev : nextHeight));
        };

        const scheduleUpdateHeight = () => {
            animationFrameIds.push(
                requestAnimationFrame(() => {
                    updateHeight();

                    animationFrameIds.push(requestAnimationFrame(updateHeight));
                }),
            );
        };

        // Call immediately once the DOM is ready
        updateHeight();
        scheduleUpdateHeight();

        PDF_HEIGHT_RECALCULATION_DELAYS_MS.forEach((delay) => {
            timeoutIds.push(window.setTimeout(updateHeight, delay));
        });

        const resizeObserver = containerRef.current ? new ResizeObserver(scheduleUpdateHeight) : null;

        if (containerRef.current && resizeObserver) {
            resizeObserver.observe(containerRef.current);
        }

        // Update on resize
        window.addEventListener('resize', scheduleUpdateHeight);

        return () => {
            animationFrameIds.forEach((animationFrameId) => cancelAnimationFrame(animationFrameId));
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
            resizeObserver?.disconnect();
            window.removeEventListener('resize', scheduleUpdateHeight);
        };
    }, [isFullscreen, pdf, scale, rotation]);

    useEffect(() => {
        const containerEl = containerRef.current;
        const holder = containerEl?.querySelector('.rc-virtual-list-holder') as HTMLElement | null;
        if (!containerEl || !holder) return;

        const canScroll = () => holder.scrollHeight > holder.clientHeight;
        const atTop = () => holder.scrollTop <= 0;
        const atBottom = () => Math.ceil(holder.scrollTop + holder.clientHeight) >= holder.scrollHeight;

        // 1) Wheel inside the HOLDER (prevent scroll chaining at edges)
        const onHolderWheel = (e: WheelEvent) => {
            if (!canScroll()) return;
            const upAtTop = e.deltaY < 0 && atTop();

            if (upAtTop) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        // 2) Wheel on the CONTAINER (outside holder but inside wrapper) → proxy to holder
        const onContainerWheel = (e: WheelEvent) => {
            if (holder.contains(e.target as Node)) return;
            if (!canScroll()) return;

            e.preventDefault();
            e.stopPropagation();
            holder.scrollTop += e.deltaY;
        };

        // 3) Touch support (holder)
        let touchYHolder = 0;
        const onHolderTouchStart = (e: TouchEvent) => (touchYHolder = e.touches[0].clientY);
        const onHolderTouchMove = (e: TouchEvent) => {
            if (!canScroll()) return;
            const y = e.touches[0].clientY;
            const delta = touchYHolder - y;
            touchYHolder = y;

            const upAtTop = delta < 0 && atTop();
            const downAtBottom = delta > 0 && atBottom();
            if (upAtTop || downAtBottom) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        // 4) Touch support (container → proxy to holder)
        let touchYContainer = 0;
        const onContainerTouchStart = (e: TouchEvent) => (touchYContainer = e.touches[0].clientY);
        const onContainerTouchMove = (e: TouchEvent) => {
            if (holder.contains(e.target as Node)) return;
            if (!canScroll()) return;

            const y = e.touches[0].clientY;
            const delta = touchYContainer - y;
            touchYContainer = y;

            e.preventDefault();
            e.stopPropagation();
            holder.scrollTop += delta;
        };

        holder.addEventListener('wheel', onHolderWheel, { passive: false });
        holder.addEventListener('touchstart', onHolderTouchStart, { passive: true });
        holder.addEventListener('touchmove', onHolderTouchMove, { passive: false });

        containerEl.addEventListener('wheel', onContainerWheel, { passive: false });
        containerEl.addEventListener('touchstart', onContainerTouchStart, { passive: true });
        containerEl.addEventListener('touchmove', onContainerTouchMove, { passive: false });

        return () => {
            holder.removeEventListener('wheel', onHolderWheel as any);
            holder.removeEventListener('touchstart', onHolderTouchStart as any);
            holder.removeEventListener('touchmove', onHolderTouchMove as any);

            containerEl.removeEventListener('wheel', onContainerWheel as any);
            containerEl.removeEventListener('touchstart', onContainerTouchStart as any);
            containerEl.removeEventListener('touchmove', onContainerTouchMove as any);
        };
    }, [pdf]);

    const VIRTUAL_LIST_SIDEBAR_HEIGHT = useMemo(
        () => computeVirtualListSidebarHeight(window.innerWidth, window.innerHeight),
        [],
    );

    //////////////////////////// After all hooks, avoid SSR render //////////////////////////////////
    if (!isMounted.current) {
        return null;
    }

    // Keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!pdf) return;

        let targetPage = pageNum;

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            if (pageNum < pdfPages) targetPage = pageNum + 1;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (pageNum > 1) targetPage = pageNum - 1;
        }

        if (targetPage !== pageNum) {
            scrollToPage(targetPage);
        }
    };

    const printPdf = () => openPdfInNewTab(pdf);
    const downloadPdf = () => downloadPdfFile(pdf, pdfTitle);

    // navigation handlers
    const prevPage = () => {
        if (pageNum > 1) scrollToPage(pageNum - 1);
    };
    const nextPage = () => {
        if (pageNum < pdfPages) scrollToPage(pageNum + 1);
    };

    const onPageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;

        if (raw === '') return;

        const value = Number(raw);

        if (value >= 1 && value <= pdfPages) {
            scrollToPage(value);
        } else if (value > pdfPages) {
            scrollToPage(pdfPages);
        }
    };

    // Zoom handlers
    const handleZoomIn = () => {
        const next = Math.round((scale + 0.1) * 100) / 100;
        setScale(next);
        setScaleOption(`${Math.round(next * 100)}%`);
    };
    const handleZoomOut = () => {
        const next = Math.round(Math.max(scale - 0.1, 0.1) * 100) / 100;
        setScale(next);
        setScaleOption(`${Math.round(next * 100)}%`);
    };

    // Page handlers
    const goFirstPage = () => scrollToPage(1);
    const goLastPage = () => scrollToPage(pdfPages);

    // Rotate handlers
    const rotateCW = () => setRotation((r) => (r + 90) % 360);
    const rotateCCW = () => setRotation((r) => (r + 270) % 360);

    // Bookmark handler
    const onBookmarkClick = () => {
        void bookmarkCurrentPage(pageNum);
    };

    return (
        <ReaderWrapper id={READER_WRAPPER_ID}>
            <ToolbarContainer>
                <ToolbarRow>
                    <Group>
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.TOGGLE_SIDEBAR) && (
                            <ToggleActionIcon
                                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                $toggled={isSidebarOpen}
                                title={t`Toggle sidebar`}
                            >
                                {isSidebarOpen ? <ListBtnIcon /> : <ListBtnIcon />}
                            </ToggleActionIcon>
                        )}
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.PREV) && (
                            <>
                                <Separator />
                                <IconButton onClick={prevPage} title={t`Previous page`}>
                                    {pageNum <= 1 ? <PrevIconInactive /> : <PrevIconActive />}
                                </IconButton>
                            </>
                        )}
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.NEXT) && (
                            <>
                                <Separator />
                                <IconButton onClick={nextPage} title={t`Next page`}>
                                    {pageNum >= pdfPages ? <NextIconInactive /> : <NextIconActive />}
                                </IconButton>
                            </>
                        )}
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.PAGE_COUNT) && (
                            <>
                                <Separator />
                                <NumberInput
                                    type="number"
                                    min={1}
                                    max={pdfPages}
                                    value={pageNum}
                                    onChange={onPageInput}
                                    id={'current-page-input'}
                                />
                                <Label>
                                    {t`of`} {pdfPages}
                                </Label>
                            </>
                        )}
                    </Group>

                    <Group>
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.ZOOM_OUT) && (
                            <IconButton onClick={handleZoomOut} title={t`Zoom out`} disabled={scale <= 0.1}>
                                <ZoomOutIcon />
                            </IconButton>
                        )}
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.ZOOM_IN) && (
                            <>
                                <Separator />
                                <IconButton onClick={handleZoomIn} title={t`Zoom in`}>
                                    <ZoomInIcon />
                                </IconButton>
                            </>
                        )}
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.SCALE_SELECT) && (
                            <>
                                <Separator />
                                <ScaleSelect
                                    suffix={<ChevronDownIcon />}
                                    value={scaleOption}
                                    options={scaleSelectOptions}
                                    onChange={handleScaleChange}
                                    classNames={{
                                        popup: {
                                            root: 'pdf-viewer-page-width-selector',
                                        },
                                    }}
                                />
                            </>
                        )}
                    </Group>

                    <Group>
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.PRINT) && (
                            <IconButton onClick={printPdf} title={t`Print`}>
                                <PrinterIcon />
                            </IconButton>
                        )}
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.DOWNLOAD) && (
                            <>
                                <Separator />
                                <IconButton onClick={downloadPdf} title={t`Download`}>
                                    <DownloadIcon />
                                </IconButton>
                            </>
                        )}
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.FULLSCREEN) && (
                            <>
                                <Separator />
                                <IconButton
                                    onClick={enterFullscreen}
                                    disabled={!isModernBrowser}
                                    title={t`Presentation mode`}
                                >
                                    <PresentationModeIcon />
                                </IconButton>
                            </>
                        )}
                        {toolbarOptions.includes(TOOLBAR_OPTIONS.EXTRA_TOOLS) && (
                            <>
                                <Separator />
                                <ToggleActionIcon
                                    $toggled={extraMenuOpen}
                                    onClick={() => setExtraMenuOpen(!extraMenuOpen)}
                                >
                                    <ToolsIcon />
                                </ToggleActionIcon>
                            </>
                        )}
                        {!!extraMenuOpen && (
                            <SecondaryMenu ref={secondaryMenuRef}>
                                <MenuItem onClick={onBookmarkClick}>
                                    <BookmarkIcon />
                                    <Label>{t`Bookmark`}</Label>
                                </MenuItem>
                                <SecondaryMenuDivider />
                                <MenuItem onClick={goFirstPage}>
                                    <ChevronsLeftIcon />
                                    <Label>{t`Go to First Page`}</Label>
                                </MenuItem>
                                <MenuItem onClick={goLastPage}>
                                    <ChevronsRightIcon />
                                    <Label>{t`Go to Last Page`}</Label>
                                </MenuItem>
                                <SecondaryMenuDivider />
                                <MenuItem onClick={rotateCW}>
                                    <RotateCWIcon />
                                    <Label>{t`Rotate CW`}</Label>
                                </MenuItem>
                                <MenuItem onClick={rotateCCW}>
                                    <RotateCCWIcon />
                                    <Label>{t`Rotate CCW`}</Label>
                                </MenuItem>
                            </SecondaryMenu>
                        )}
                    </Group>
                </ToolbarRow>
            </ToolbarContainer>
            <DrawerWrapper
                title={t`Pages`}
                placement="left"
                size={DRAWER_WIDTH}
                getContainer={false}
                onClose={() => setIsSidebarOpen(false)}
                open={isSidebarOpen}
                forceRender
                destroyOnHidden={false}
                mask={false}
            >
                {pdf ? (
                    <VirtualList
                        data={virtualListData}
                        height={VIRTUAL_LIST_SIDEBAR_HEIGHT}
                        itemKey={(n) => n}
                        fullHeight={false}
                    >
                        {(page) => (
                            <Thumbnail
                                key={page}
                                pdf={pdf}
                                pageNumber={page}
                                isSelected={pageNum === page}
                                onClick={() => scrollToPage(page)}
                            />
                        )}
                    </VirtualList>
                ) : (
                    <ThumbnailSpinner />
                )}
            </DrawerWrapper>

            <ViewerContainer
                ref={containerRef}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                data-cy={TEST_IDS.SHARED.PDF_VIEWER}
            >
                {!!pdf && (
                    <VirtualList
                        data={virtualListData}
                        height={virtualListHeight}
                        itemKey={(page) => page}
                        fullHeight={false}
                    >
                        {(page) => (
                            <PdfPage
                                key={`page-${page}`}
                                pdf={pdf}
                                pageNumber={page}
                                scale={scale}
                                rotation={rotation}
                                onHeightReady={handlePageHeight}
                            />
                        )}
                    </VirtualList>
                )}
                {!!isFullscreen && (
                    <MinimizeButton onClick={exitFullscreen}>
                        <MinimizeIconWrapper />
                    </MinimizeButton>
                )}
            </ViewerContainer>
        </ReaderWrapper>
    );
};
